/* eslint-disable no-underscore-dangle */
import ErrorMessageProvider from '@ErrorMessageProvider';
import ScrollContainer from '@ScrollContainer';
import Script from 'next/script';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { isFunctionLazyLoadable } from '../../Utils/utils';
import InputArea from '../InputArea/InputArea';
import {
  componentIdsIncludesTypes,
  getComponentIds,
  getPageErrorMessage,
  getVisiblePageComponents,
} from './page.helpers';
import useGetExperimentName from '../../Hooks/useGetExperimentName/useGetExperimentName';

const Page = () => {
  const dispatch = useDispatch();
  const { exitPhase, enterPhase } = useSelector((store) => store.transition);
  const { pages } = useSelector((store) => store.paging);
  const components = useSelector((store) => store.components);
  const { showCpcRecommendations, wasPreviousPageCpcCard } = useSelector(
    (store) => store.cpcCard
  );
  const { currentPage, slideDirection } = useSelector(
    (store) => store.navigation
  );
  const {
    iFrameId,
    isDynamicHeightEnabled,
    haveAllInitialMessagesArrived,
    maximumWidgetHeight,
  } = useSelector((store) => store.optionalFormConfig);
  const { requiredWidgetHeight } = useSelector((store) => store.dynamicHeight);
  const { loaderConfig } = useSelector((store) => store.lottieAnimationData);
  const { dynamicNewsletterName } = useSelector((store) => store.newsletter);
  const { phoneNumberCountryCode, subcategoryId } = useSelector(
    (store) => store.formConfig
  );

  const [errorMessage, setErrorMessage] = useState(undefined);
  const [loadLottieFilesRenderer, setLoadLottieFilesRenderer] = useState(false);
  const [animationJsonFileLoaded, setAnimationJsonFileLoaded] = useState(false);
  const pageComponentIds = pages[currentPage];
  const adjustedSlideDirection = pageComponentIds ? slideDirection : 'left';

  const errorMessageProviderExperimentName = useGetExperimentName({
    componentName: 'ErrorMessageProvider',
  });

  const isDynamicHeightWithinMaximumHeight =
    isDynamicHeightEnabled === true &&
    requiredWidgetHeight < maximumWidgetHeight;
  const haveAllInitialParentPageMessagesComeIn =
    haveAllInitialMessagesArrived === true;

  const isCpcCardShowingOrTransitioningOut = !!(
    isDynamicHeightEnabled &&
    (showCpcRecommendations || wasPreviousPageCpcCard)
  );

  const scrollDisabled =
    isDynamicHeightWithinMaximumHeight ||
    !haveAllInitialParentPageMessagesComeIn ||
    isCpcCardShowingOrTransitioningOut;

  const visiblePageComponents = getVisiblePageComponents(
    pageComponentIds,
    components,
    dynamicNewsletterName,
    phoneNumberCountryCode,
    subcategoryId
  );

  const visiblePageComponentIds = getComponentIds({
    components: visiblePageComponents,
  });

  const noComponentsString = 'No components to display';

  if (visiblePageComponents.length > 0) {
    const pageError = getPageErrorMessage(visiblePageComponents);
    if (pageError !== errorMessage) {
      setErrorMessage(pageError);
    }
  }

  if (
    visiblePageComponents.length === 0 &&
    errorMessage !== noComponentsString
  ) {
    // if no components to display set error message
    setErrorMessage(noComponentsString);
  }

  const lottieAnimationRendererUrl =
    'https://unpkg.com/@lottiefiles/lottie-player@1.6.0/dist/lottie-player.js';

  useEffect(() => {
    if (
      !enterPhase &&
      currentPage !== undefined &&
      currentPage !== null &&
      !scrollDisabled
    ) {
      setTimeout(() => {
        const formContainer = document.querySelector('form');
        // Trusted form adds 3 hidden fields to the container: xxTrustedFormCertUrl, xxTrustedFormToken, xxTrustedFormPingUrl
        // This is looking for those 3 fields, plus 1 more div for the form itself
        if (formContainer?.children && formContainer.children.length === 4) {
          const formContent = formContainer.children[0];
          const scrollArea =
            formContent.children.length >= 2
              ? formContent.children[1]
              : undefined;
          if (scrollArea?.scroll && scrollArea?.scrollTop !== 0) {
            scrollArea.scroll(0, 0);
          }
        }
      }, 0);
    }
    /**
     * We are not dependent on the current page number, because it doesn't update after page change is completed, it is enterPhase that is updated to false when a page change is completed.
     *
     * currentPage should not be a dependency here as there could be a race between the enterPhase and the currentPage
     */
  }, [enterPhase]);

  useEffect(() => {
    const onContentMounting = window?.__private__?.onContentMounting;
    if (!isDynamicHeightEnabled && onContentMounting) {
      /**
       * Race condition resolution between the ScrollContainer recalculating the
       * scrollArea values (scrollHeight/clientHeight) whilst the inputArea of the
       * current page hasn't fully unmounted yet.
       * This callback function announces the beginning of the unmounting phase
       * of the inputArea followed by the mounting phase of the next page's
       * inputArea to the ScrollContainer, thus blocking any recalculations of
       * the scroll area.
       */
      onContentMounting();
    }
  }, [currentPage]);

  useEffect(() => {
    /* Note: To appease PageSpeedInsights, load lottie dependencies after user interactions
     * Load the first of two lottie dependencies after the enterPhase for reaching page 2 completes
     * Splitting up the loading of lottie resources minimises the chance for a UI lag during the chunk load
     */
    if (
      typeof currentPage === 'number' &&
      enterPhase === false &&
      currentPage > 0 &&
      !animationJsonFileLoaded &&
      !loadLottieFilesRenderer
    ) {
      /**
       * If there is no lottie animation json file, then preload it to reduce later latency and race conditions
       */
      import(
        './lazyLoadable/loadRemoteAnimationJsonFiles' /* webpackChunkName: 'loadRemoteAnimationJsonFiles' */
      )
        .then((lottieLoaderModule) => {
          if (
            isFunctionLazyLoadable({
              functionName: 'loadRemoteAnimationJsonFiles',
              module: lottieLoaderModule,
            })
          ) {
            lottieLoaderModule.loadRemoteAnimationJsonFiles(
              loaderConfig,
              dispatch
            );
            setAnimationJsonFileLoaded(true);
          }
        })
        .catch((error) => {
          /* eslint-disable no-console */
          console.log(
            'MVF App Info - loadRemoteAnimationJsonFiles failed to load:',
            error,
            '/nRe-trying shortly ...'
          );
          // Employ re-try strategy via setInterval (every 2 seconds until the request succeeds. Once it does, cleanup the interval)
          let pollingId = setInterval(() => {
            import(
              './lazyLoadable/loadRemoteAnimationJsonFiles' /* webpackChunkName: 'loadRemoteAnimationJsonFiles' */
            )
              .then((lottieLoaderModule) => {
                clearInterval(pollingId); // Prevent this code from running successfully more than once
                pollingId = null; // Release pollingId from the variable
                if (
                  isFunctionLazyLoadable({
                    functionName: 'loadRemoteAnimationJsonFiles',
                    module: lottieLoaderModule,
                  })
                ) {
                  lottieLoaderModule.loadRemoteAnimationJsonFiles(
                    loaderConfig,
                    dispatch
                  );
                  setAnimationJsonFileLoaded(true);
                }
              })
              .catch((retryError) => {
                // Do nothing and try again in 2 seconds
                /* eslint-disable no-console */
                console.log(
                  'loadRemoteAnimationJsonFiles failed to load again!:',
                  retryError,
                  '/nRe-trying shortly ...'
                );
              });
          }, 2000);
        });
    }
  }, [enterPhase]);

  useEffect(() => {
    /* Load the second of two lottie dependencies once the the first lottie dependency is loaded
     * This leverages the 400ms-time window of the Transition animation during which the UI is unresponsive anyway
     */
    if (
      typeof currentPage === 'number' &&
      animationJsonFileLoaded &&
      !loadLottieFilesRenderer
    ) {
      setLoadLottieFilesRenderer(true);
    }
  }, [animationJsonFileLoaded]);

  /**
   * default size of input area container 'md' and increased if required - but only under the conditions of being in a NEW entering phase
   */
  const [
    hasRadiosOrCheckboxesOnCurrentPage,
    setHasRadiosOrCheckboxesOnCurrentPage,
  ] = useState(
    !!componentIdsIncludesTypes({
      components,
      types: ['radios', 'checkboxes'],
      componentIds: visiblePageComponentIds,
    })
  );

  useEffect(() => {
    if (!exitPhase && enterPhase) {
      const mdOrLg = !!componentIdsIncludesTypes({
        components,
        types: ['radios', 'checkboxes'],
        componentIds: visiblePageComponentIds,
      });

      setHasRadiosOrCheckboxesOnCurrentPage(mdOrLg);
    }
  }, [exitPhase, enterPhase]);
  return (
    <ErrorMessageProvider
      errorMessage={errorMessage}
      experimentName={errorMessageProviderExperimentName}
    >
      {scrollDisabled ? (
        <div
          id="inputAreaParentContainer"
          style={{
            height: '0',
            flex: '1 1 auto',
            width: '100%',
            display: 'block',
            overflow: 'hidden',
            paddingLeft: '10px',
            paddingRight: '10px',
          }}
        >
          <InputArea
            components={components}
            hasRadiosOrCheckboxesOnCurrentPage={
              hasRadiosOrCheckboxesOnCurrentPage
            }
            currentPage={currentPage}
            iFrameId={iFrameId}
            pageComponentIds={pageComponentIds}
            slideDirection={adjustedSlideDirection}
            visiblePageComponents={visiblePageComponents}
          />
        </div>
      ) : (
        <ScrollContainer data-testid="scrollContainer">
          <InputArea
            components={components}
            currentPage={currentPage}
            hasRadiosOrCheckboxesOnCurrentPage={
              hasRadiosOrCheckboxesOnCurrentPage
            }
            iFrameId={iFrameId}
            pageComponentIds={pageComponentIds}
            slideDirection={adjustedSlideDirection}
            visiblePageComponents={visiblePageComponents}
          />
        </ScrollContainer>
      )}
      {loadLottieFilesRenderer && (
        <Script
          src={lottieAnimationRendererUrl}
          onLoad={() => {
            import(
              './lazyLoadable/dispatchAnimationData' /* webpackChunkName: 'dispatchAnimationData' */
            ).then((animationDispatchModule) => {
              if (
                isFunctionLazyLoadable({
                  functionName: 'dispatchAnimationData',
                  module: animationDispatchModule,
                })
              ) {
                animationDispatchModule.dispatchAnimationData({
                  dispatch,
                  payload: {
                    isRendererAvailable: true,
                  },
                });
              }
            });
          }}
          onError={() => {
            console.log('MVF App Info - Failed to fetch optional dependency');
            // Employ re-try strategy via setInterval (every 2 seconds until the request succeeds. Once it does, cleanup the interval)
            let pollingId = setInterval(() => {
              import(
                './lazyLoadable/dispatchAnimationData' /* webpackChunkName: 'dispatchAnimationData' */
              )
                .then((animationDispatchModule) => {
                  clearInterval(pollingId); // Prevent this code from running successfully more than once
                  pollingId = null; // Release pollingId from the variable
                  if (
                    isFunctionLazyLoadable({
                      functionName: 'dispatchAnimationData',
                      module: animationDispatchModule,
                    })
                  ) {
                    animationDispatchModule.dispatchAnimationData({
                      dispatch,
                      payload: {
                        isRendererAvailable: true,
                      },
                    });
                  }
                })
                .catch((error) => {
                  // Do nothing and try again in 2 seconds
                  console.log(
                    'MVF App Info - dispatchAnimationData failed to load:',
                    error,
                    '/nRe-trying shortly ...'
                  );
                });
            }, 2000);
          }}
        />
      )}
    </ErrorMessageProvider>
  );
};

export default Page;
